import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { CompetitionEntries } from '../model/clubPlayers';
import type {
  AddParticipantByOrganizerRequest,
  ApiBracket,
  ApiCompetition,
  ApiCourt,
  ApiMatch,
  ApiParticipant,
  ApiRoundRobinDraw,
  ApiStanding,
  ClubDashboard,
  CreateCompetitionRequest,
  CreateConsolationRequest,
  UpdateCompetitionPayload,
} from '../model/types';
import {
  addParticipantByOrganizer,
  advanceToFinalPhase,
  cancelCompetition,
  deleteCompetition,
  copyCompetition,
  createCompetition,
  createConsolation,
  drawCompetition,
  editMatchScore,
  editMatchWalkover,
  enterMatchScore,
  getBracket,
  getClubCompetitions,
  getClubDashboard,
  getClubMatches,
  getCompetition,
  getCompetitionCourts,
  getCompetitionMatches,
  getMatch,
  getParticipants,
  getStandings,
  matchWalkover,
  removeParticipant,
  runCompetitionAction,
  runMatchAction,
  setCompetitionVisibility,
  setParticipantSeed,
  updateCompetition,
  type ClubMatchesFilter,
  type CompetitionLifecycleAction,
  type MatchLifecycleAction,
  type SetScorePayload,
} from './competitionApi';

/** Co ile odswiezamy dane operacyjne dashboardu (mecze na zywo, kafelki). */
const DASHBOARD_REFRESH_MS = 30_000;

/** Klucze pamieci podrecznej widokow klubowych. */
export const competitionKeys = {
  all: ['competitions'] as const,
  clubDashboard: (clubId: string) => [...competitionKeys.all, 'club', clubId, 'dashboard'] as const,
  clubMatches: (clubId: string, filter: ClubMatchesFilter) =>
    [
      ...competitionKeys.all,
      'club',
      clubId,
      'matches',
      filter.date ?? '',
      filter.status ?? '',
    ] as const,
  clubCompetitions: (clubId: string) => [...competitionKeys.all, 'club', clubId, 'list'] as const,
  detail: (competitionId: string) => [...competitionKeys.all, 'detail', competitionId] as const,
  participants: (competitionId: string) =>
    [...competitionKeys.all, 'detail', competitionId, 'participants'] as const,
  bracket: (competitionId: string) =>
    [...competitionKeys.all, 'detail', competitionId, 'bracket'] as const,
  matches: (competitionId: string) =>
    [...competitionKeys.all, 'detail', competitionId, 'matches'] as const,
  courts: (competitionId: string) =>
    [...competitionKeys.all, 'detail', competitionId, 'courts'] as const,
  standings: (competitionId: string) =>
    [...competitionKeys.all, 'detail', competitionId, 'standings'] as const,
};

/** Klucze pamieci podrecznej pojedynczego meczu. */
export const matchKeys = {
  all: ['match'] as const,
  detail: (matchId: string) => [...matchKeys.all, matchId] as const,
};

/** Pobiera agregat dashboardu klubu; odswieza sie w tle co pol minuty. */
export function useClubDashboardQuery(clubId: string | undefined): UseQueryResult<ClubDashboard> {
  return useQuery({
    queryKey: competitionKeys.clubDashboard(clubId ?? ''),
    queryFn: () => getClubDashboard(clubId ?? ''),
    enabled: Boolean(clubId),
    refetchInterval: DASHBOARD_REFRESH_MS,
  });
}

/** Pobiera mecze klubu z opcjonalnymi filtrami daty i statusu. */
export function useClubMatchesQuery(
  clubId: string | undefined,
  filter: ClubMatchesFilter = {},
): UseQueryResult<ApiMatch[]> {
  return useQuery({
    queryKey: competitionKeys.clubMatches(clubId ?? '', filter),
    queryFn: () => getClubMatches(clubId ?? '', filter),
    enabled: Boolean(clubId),
    refetchInterval: DASHBOARD_REFRESH_MS,
  });
}

/**
 * Pobiera pelna liste rozgrywek klubu na potrzeby widoku turniejow.
 *
 * Bez odswiezania w tle — lista zmienia sie rzadko, a widok i tak uniewaznia
 * zapytanie po kazdej akcji zmieniajacej stan rozgrywki.
 */
export function useClubCompetitionsQuery(
  clubId: string | undefined,
): UseQueryResult<ApiCompetition[]> {
  return useQuery({
    queryKey: competitionKeys.clubCompetitions(clubId ?? ''),
    queryFn: () => getClubCompetitions(clubId ?? ''),
    enabled: Boolean(clubId),
  });
}

/**
 * Zaklada rozgrywke i uniewaznia widoki klubu, zeby lista pokazala ja od razu
 * po powrocie z formularza.
 */
export function useCreateCompetitionMutation(
  clubId: string | undefined,
): UseMutationResult<void, Error, CreateCompetitionRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateCompetitionRequest) => createCompetition(request),
    onSuccess: () => {
      if (!clubId) return;
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubCompetitions(clubId) });
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubDashboard(clubId) });
    },
  });
}

/** Dane kopiowania rozgrywki: zrodlo i nazwa nowego turnieju. */
export interface CopyCompetitionVariables {
  competitionId: string;
  /** Nazwa kopii — jedyne, co organizator podaje przy kopiowaniu. */
  name: string;
}

/**
 * Kopiuje rozgrywke i uniewaznia widoki klubu, zeby kopia pojawila sie na liscie
 * od razu — zawsze z otwartymi zapisami, niezaleznie od stanu oryginalu.
 */
export function useCopyCompetitionMutation(
  clubId: string | undefined,
): UseMutationResult<void, Error, CopyCompetitionVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ competitionId, name }: CopyCompetitionVariables) =>
      copyCompetition(competitionId, name),
    onSuccess: () => {
      if (!clubId) return;
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubCompetitions(clubId) });
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubDashboard(clubId) });
    },
  });
}

/** Dane przelaczenia widocznosci: rozgrywka i docelowa widocznosc. */
export interface SetVisibilityVariables {
  competitionId: string;
  visibility: 'PUBLIC' | 'PRIVATE';
}

/**
 * Przelacza widocznosc rozgrywki (PUBLIC/PRIVATE) — dozwolone w kazdym statusie.
 * Uniewaznia liste klubu, dashboard i szczegoly, zeby przelacznik i widok
 * publiczny odswiezyly sie od razu.
 */
export function useSetVisibilityMutation(
  clubId: string | undefined,
): UseMutationResult<void, Error, SetVisibilityVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ competitionId, visibility }: SetVisibilityVariables) =>
      setCompetitionVisibility(competitionId, visibility),
    onSuccess: (_data, { competitionId }) => {
      void queryClient.invalidateQueries({ queryKey: competitionKeys.detail(competitionId) });
      if (!clubId) return;
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubCompetitions(clubId) });
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubDashboard(clubId) });
    },
  });
}

/** Dane tworzenia turnieju pocieszenia: turniej zasadniczy i parametry organizatora. */
export interface CreateConsolationVariables {
  /** Turniej zasadniczy, z ktorego przegranych 1. rundy powstaje pocieszenie. */
  competitionId: string;
  request: CreateConsolationRequest;
}

/**
 * Tworzy turniej pocieszenia i uniewaznia widoki klubu — pocieszenie powstaje
 * od razu w toku, z gotowa drabinka z przegranych 1. rundy.
 */
export function useCreateConsolationMutation(
  clubId: string | undefined,
): UseMutationResult<void, Error, CreateConsolationVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ competitionId, request }: CreateConsolationVariables) =>
      createConsolation(competitionId, request),
    onSuccess: () => {
      if (!clubId) return;
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubCompetitions(clubId) });
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubDashboard(clubId) });
    },
  });
}

/**
 * Aktualizuje ustawienia rozgrywki i uniewaznia jej widoki — szczegoly,
 * liste klubu i kafelki dashboardu.
 */
export function useUpdateCompetitionMutation(
  competitionId: string,
  clubId: string | undefined,
): UseMutationResult<void, Error, UpdateCompetitionPayload> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateCompetitionPayload) => updateCompetition(competitionId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: competitionKeys.detail(competitionId) });
      if (clubId) {
        void queryClient.invalidateQueries({ queryKey: competitionKeys.clubCompetitions(clubId) });
        void queryClient.invalidateQueries({ queryKey: competitionKeys.clubDashboard(clubId) });
      }
    },
  });
}

/** Pobiera pojedyncza rozgrywke — naglowek ekranu zawodnikow. */
export function useCompetitionQuery(
  competitionId: string | undefined,
): UseQueryResult<ApiCompetition> {
  return useQuery({
    queryKey: competitionKeys.detail(competitionId ?? ''),
    queryFn: () => getCompetition(competitionId ?? ''),
    enabled: Boolean(competitionId),
  });
}

/** Pobiera zgloszenia do rozgrywki. */
export function useParticipantsQuery(
  competitionId: string | undefined,
): UseQueryResult<ApiParticipant[]> {
  return useQuery({
    queryKey: competitionKeys.participants(competitionId ?? ''),
    queryFn: () => getParticipants(competitionId ?? ''),
    enabled: Boolean(competitionId),
  });
}

/**
 * Pobiera zgloszenia wszystkich wskazanych rozgrywek naraz.
 *
 * API nie ma endpointu z zawodnikami klubu, wiec liste skladamy ze zgloszen
 * poszczegolnych rozgrywek — jedno zapytanie na rozgrywke, rownolegle.
 * Rozgrywka, ktorej zgloszen nie da sie pobrac (np. 403), wchodzi z pusta lista:
 * pojedynczy blad nie moze wygasic calego ekranu.
 */
export function useCompetitionEntriesQuery(
  competitions: readonly ApiCompetition[],
): UseQueryResult<CompetitionEntries[]> {
  const ids = competitions.map((competition) => competition.id).sort();

  return useQuery({
    queryKey: [...competitionKeys.all, 'entries', ids.join(',')],
    queryFn: async () => {
      const lists = await Promise.all(
        competitions.map((competition) => getParticipants(competition.id).catch(() => [])),
      );
      return competitions.map((competition, index) => ({
        competition,
        participants: lists[index] ?? [],
      }));
    },
    enabled: competitions.length > 0,
  });
}

/**
 * Pobiera drabinke pucharowa. Wlaczane jawnie — przed losowaniem (i w systemach
 * bez drabinki) endpoint odpowiada bledem, wiec zapytanie ma sens dopiero, gdy
 * rozgrywka ma `bracketSize`.
 */
export function useBracketQuery(
  competitionId: string | undefined,
  enabled: boolean,
): UseQueryResult<ApiBracket> {
  return useQuery({
    queryKey: competitionKeys.bracket(competitionId ?? ''),
    queryFn: () => getBracket(competitionId ?? ''),
    enabled: Boolean(competitionId) && enabled,
  });
}

/** Pobiera wszystkie mecze rozgrywki — widok grup i kolejek po losowaniu. */
export function useCompetitionMatchesQuery(
  competitionId: string | undefined,
  enabled = true,
): UseQueryResult<ApiMatch[]> {
  return useQuery({
    queryKey: competitionKeys.matches(competitionId ?? ''),
    queryFn: () => getCompetitionMatches(competitionId ?? ''),
    enabled: Boolean(competitionId) && enabled,
  });
}

/**
 * Uniewaznia wszystko, co zmienia losowanie: rozgrywke (status, bracketSize),
 * zgloszenia (przydzial grup, aktywacja), drabinke, mecze i widoki klubowe.
 */
function useDrawInvalidation(competitionId: string, clubId: string | undefined) {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: competitionKeys.detail(competitionId) });
    void queryClient.invalidateQueries({ queryKey: competitionKeys.participants(competitionId) });
    void queryClient.invalidateQueries({ queryKey: competitionKeys.bracket(competitionId) });
    void queryClient.invalidateQueries({ queryKey: competitionKeys.matches(competitionId) });
    void queryClient.invalidateQueries({ queryKey: competitionKeys.standings(competitionId) });
    if (clubId) {
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubDashboard(clubId) });
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubCompetitions(clubId) });
    }
  };
}

/** Losuje drabinke / grupy (`POST /api/competitions/{id}/draw`). */
export function useDrawMutation(
  competitionId: string,
  clubId: string | undefined,
): UseMutationResult<ApiBracket | ApiRoundRobinDraw, Error, void> {
  const invalidate = useDrawInvalidation(competitionId, clubId);

  return useMutation({
    mutationFn: () => drawCompetition(competitionId),
    onSuccess: invalidate,
  });
}

/** Losuje faze finalowa GROUP_PLAYOFF (`POST /api/competitions/{id}/advance`). */
export function useAdvanceMutation(
  competitionId: string,
  clubId: string | undefined,
): UseMutationResult<ApiBracket | ApiRoundRobinDraw, Error, void> {
  const invalidate = useDrawInvalidation(competitionId, clubId);

  return useMutation({
    mutationFn: () => advanceToFinalPhase(competitionId),
    onSuccess: invalidate,
  });
}

/* --- Pojedynczy mecz i wprowadzanie wyniku --- */

/** Pobiera pojedynczy mecz — ekran wprowadzania wyniku. */
export function useMatchQuery(matchId: string | undefined): UseQueryResult<ApiMatch> {
  return useQuery({
    queryKey: matchKeys.detail(matchId ?? ''),
    queryFn: () => getMatch(matchId ?? ''),
    enabled: Boolean(matchId),
  });
}

/** Pobiera korty turniejowe rozgrywki (nazwy do etykiety „Kort 1”). */
export function useCourtsQuery(competitionId: string | undefined): UseQueryResult<ApiCourt[]> {
  return useQuery({
    queryKey: competitionKeys.courts(competitionId ?? ''),
    queryFn: () => getCompetitionCourts(competitionId ?? ''),
    enabled: Boolean(competitionId),
  });
}

/** Pobiera tabele rozgrywki (punkty, W/P, bilans gemów, rank per grupa). */
export function useStandingsQuery(
  competitionId: string | undefined,
  enabled = true,
): UseQueryResult<ApiStanding[]> {
  return useQuery({
    queryKey: competitionKeys.standings(competitionId ?? ''),
    queryFn: () => getStandings(competitionId ?? ''),
    enabled: Boolean(competitionId) && enabled,
  });
}

/**
 * Uniewaznia wszystko, co zmienia wynik meczu: sam mecz, mecze i drabinke
 * rozgrywki (awans zwyciezcy), liste zgloszen (statusy ELIMINATED)
 * oraz widoki klubowe.
 */
function useMatchInvalidation(
  matchId: string,
  competitionId: string | undefined,
  clubId: string | undefined,
) {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: matchKeys.detail(matchId) });
    if (competitionId) {
      void queryClient.invalidateQueries({ queryKey: competitionKeys.matches(competitionId) });
      void queryClient.invalidateQueries({ queryKey: competitionKeys.bracket(competitionId) });
      void queryClient.invalidateQueries({
        queryKey: competitionKeys.participants(competitionId),
      });
      // Rozstrzygnięty mecz przelicza tabele grup.
      void queryClient.invalidateQueries({ queryKey: competitionKeys.standings(competitionId) });
    }
    if (clubId) {
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubDashboard(clubId) });
    }
  };
}

/** Zmienne mutacji wyniku: sety i tryb (nowy wynik albo korekta). */
export interface EnterScoreVariables {
  scores: SetScorePayload[];
  /** `true` = `PUT /score` (korekta, tylko organizator). */
  edit: boolean;
}

/** Wpisuje albo poprawia wynik meczu. */
export function useEnterScoreMutation(
  matchId: string,
  competitionId: string | undefined,
  clubId: string | undefined,
): UseMutationResult<void, Error, EnterScoreVariables> {
  const invalidate = useMatchInvalidation(matchId, competitionId, clubId);

  return useMutation({
    mutationFn: ({ scores, edit }: EnterScoreVariables) =>
      edit ? editMatchScore(matchId, scores) : enterMatchScore(matchId, scores),
    onSuccess: invalidate,
  });
}

/** Zmienne mutacji walkowera: zwycieska strona i tryb (nowy walkower albo korekta). */
export interface WalkoverVariables {
  winningSide: 1 | 2;
  /** `true` = `PUT /walkover` (korekta meczu juz rozstrzygnietego, tylko organizator). */
  edit: boolean;
}

/** Orzeka albo poprawia walkower dla wskazanej strony meczu. */
export function useWalkoverMutation(
  matchId: string,
  competitionId: string | undefined,
  clubId: string | undefined,
): UseMutationResult<void, Error, WalkoverVariables> {
  const invalidate = useMatchInvalidation(matchId, competitionId, clubId);

  return useMutation({
    mutationFn: ({ winningSide, edit }: WalkoverVariables) =>
      edit ? editMatchWalkover(matchId, winningSide) : matchWalkover(matchId, winningSide),
    onSuccess: invalidate,
  });
}

/** Zmienia status meczu: start / zawieszenie / wznowienie. */
export function useMatchActionMutation(
  matchId: string,
  competitionId: string | undefined,
  clubId: string | undefined,
): UseMutationResult<void, Error, MatchLifecycleAction> {
  const invalidate = useMatchInvalidation(matchId, competitionId, clubId);

  return useMutation({
    mutationFn: (action: MatchLifecycleAction) => runMatchAction(matchId, action),
    onSuccess: invalidate,
  });
}

/**
 * Uniewaznia wszystko, co zalezy od listy zgloszen: sama lista, rozgrywka
 * (liczniki) oraz widoki klubowe.
 */
function useParticipantInvalidation(competitionId: string, clubId: string | undefined) {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: competitionKeys.participants(competitionId) });
    void queryClient.invalidateQueries({ queryKey: competitionKeys.detail(competitionId) });
    if (clubId) {
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubDashboard(clubId) });
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubCompetitions(clubId) });
    }
  };
}

/** Dodaje slot w imieniu organizatora. */
export function useAddParticipantMutation(
  competitionId: string,
  clubId: string | undefined,
): UseMutationResult<void, Error, AddParticipantByOrganizerRequest> {
  const invalidate = useParticipantInvalidation(competitionId, clubId);

  return useMutation({
    mutationFn: (request: AddParticipantByOrganizerRequest) =>
      addParticipantByOrganizer(competitionId, request),
    onSuccess: invalidate,
  });
}

/** Usuwa zgloszenie po identyfikatorze dowolnego czlonka slotu. */
export function useRemoveParticipantMutation(
  competitionId: string,
  clubId: string | undefined,
): UseMutationResult<void, Error, string> {
  const invalidate = useParticipantInvalidation(competitionId, clubId);

  return useMutation({
    mutationFn: (userId: string) => removeParticipant(competitionId, userId),
    onSuccess: invalidate,
  });
}

/** Wynik masowego usuwania zgloszen. */
export interface RemoveParticipantsResult {
  /** Liczba zgloszen usunietych poprawnie. */
  removed: number;
  /** Liczba zgloszen, ktorych nie udalo sie usunac. */
  failed: number;
}

/**
 * Usuwa wiele zgloszen naraz (zaznaczenie na liscie zawodnikow).
 *
 * Backend nie ma zbiorczego endpointu, wiec wolamy DELETE per zgloszenie —
 * w paczkach po kilka rownolegle, zeby setka zaznaczen nie otworzyla stu
 * polaczen naraz. Bledy pojedynczych usuniec nie przerywaja reszty; wynik
 * podsumowuje, ile sie udalo. Cache uniewazniamy raz, po calej serii.
 */
export function useRemoveParticipantsMutation(
  competitionId: string,
  clubId: string | undefined,
): UseMutationResult<RemoveParticipantsResult, Error, string[]> {
  const invalidate = useParticipantInvalidation(competitionId, clubId);

  return useMutation({
    mutationFn: async (userIds: string[]) => {
      const BATCH_SIZE = 5;
      let failed = 0;
      for (let start = 0; start < userIds.length; start += BATCH_SIZE) {
        const settled = await Promise.allSettled(
          userIds
            .slice(start, start + BATCH_SIZE)
            .map((userId) => removeParticipant(competitionId, userId)),
        );
        failed += settled.filter((result) => result.status === 'rejected').length;
      }
      return { removed: userIds.length - failed, failed };
    },
    onSettled: invalidate,
  });
}

/** Ustawia rozstawienie zawodnika. */
export function useSetSeedMutation(
  competitionId: string,
  clubId: string | undefined,
): UseMutationResult<void, Error, { userId: string; seed: number | null }> {
  const invalidate = useParticipantInvalidation(competitionId, clubId);

  return useMutation({
    mutationFn: ({ userId, seed }: { userId: string; seed: number | null }) =>
      setParticipantSeed(competitionId, userId, seed),
    onSuccess: invalidate,
  });
}

/** Zmiana stanu rozgrywki zlecana z listy turniejow. */
export interface CompetitionActionVariables {
  competitionId: string;
  /**
   * `cancel` → `POST /{id}/cancel` (status CANCELLED, rozgrywka dalej widoczna),
   * `delete` → `DELETE /{id}` (miekkie usuniecie — znika ze wszystkich widokow),
   * reszta → `POST /{id}/{akcja}`.
   */
  action: CompetitionLifecycleAction | 'cancel' | 'delete';
}

/**
 * Wykonuje akcje cyklu zycia rozgrywki i uniewaznia widoki klubu,
 * zeby lista i kafelki dashboardu od razu pokazaly nowy stan.
 */
export function useCompetitionActionMutation(
  clubId: string | undefined,
): UseMutationResult<void, Error, CompetitionActionVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ competitionId, action }: CompetitionActionVariables) =>
      action === 'cancel'
        ? cancelCompetition(competitionId)
        : action === 'delete'
          ? deleteCompetition(competitionId)
          : runCompetitionAction(competitionId, action),
    onSuccess: () => {
      if (!clubId) return;
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubCompetitions(clubId) });
      void queryClient.invalidateQueries({ queryKey: competitionKeys.clubDashboard(clubId) });
    },
  });
}
